import React from 'react';

import imageCompression from 'browser-image-compression';

// @material-ui/core components
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActionArea from '@material-ui/core/CardActionArea';
import Typography from '@material-ui/core/Typography';
import Slide from '@material-ui/core/Slide';
import { TransitionProps } from '@material-ui/core/transitions';
import { GridContainer, GridItem, AlertDialog as PopUp, CustomButton } from '../../components';
import { makeStyles } from '@material-ui/core/styles';

import CloseIcon from '@material-ui/icons/Close';
import CancelIcon from '@material-ui/icons/Cancel';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
// import myInfoIcon from '../../images/myinfo.png';
import imageProcessIcon from '../../images/image-process.gif';
import facialIcon from '../../images/capture-selfie.gif';
import documentIcon from '../../images/id-scan.gif';

//Helper
import { convertFileToBase64, checkSupportedUploadImage } from '../../utils/fileHelper';
import {
  ScanDocument,
  ScanningUser,
  Image,
  DocumentTypeList,
  convertToUpdateGuestRequestModel,
  User,
  UpdateGuestResponseModel,
  ARIA_SuccessCode,
  convertToCreateGuestRequestModel
} from '../../models';

//Service
import { livenessDetection, faceMatch } from '../../services/facialDetectService';
// import { postDocumentForOCR } from '../../services/docscanService';
import { RootState } from 'reducers';
import { useDispatch, useSelector } from 'react-redux';
import { getVerbiage } from 'utils/stringHelper';
import Button from '@material-ui/core/Button';
import { combineFrontAndBackDocument, ConvertToScanDocument } from 'utils/DocumentHelper';
import { updateGuestAPI } from 'services/guestService';
import { addNewGuestToReservation, reservationVerified, updateReservationGuest } from 'reducers/reservation';
import { verifyReservationAPI } from 'services/reservationService';
import { orcIdentityDocumentAPI } from 'services/identityService';
import { postDocumentForOCR } from 'services/docscanService';
import { getCountryCode3ByName } from 'utils/countryHelper';
import { postErrorLog } from 'services/errorLogService';
import { getApplicationMode, getCameraDirection } from 'utils/applicationHelper';
import { FrontCameraDirection, RearCameraDirection, FacingFrontMode, FacingRearMode } from 'utils/constantHelper';
import { KioskApplicationMode } from 'utils/constantHelper';

//Mock
// import { mockScanDocument1, mockFacialResult } from '../../mock/document';

const useStyles = makeStyles((theme) => ({
  subtitle: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  appBar: {
    position: 'fixed',
    backgroundColor: theme.palette.primary.main
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1
  }
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const imageCompressionOptions = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true
};

interface IProps extends DialogProps {
  updateUser: User | undefined;
  onCompleted: (isCompleted: boolean, guest: ScanningUser | null) => void;
}

export const CaptureDocumentDialog: React.FC<IProps> = ({ updateUser, onCompleted, open, children, ...rest }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { appConfig } = useSelector((state: RootState) => state.appConfigState);
  const { selectedHotel } = useSelector((state: RootState) => state.hotelState);
  const { verbiages } = useSelector((state: RootState) => state.verbiages);
  const { selectedReservation } = useSelector((state: RootState) => state.reservationState);
  const { countryList } = useSelector((state: RootState) => state.country);

  const documentInputFileRef = React.useRef<HTMLInputElement>(null);
  const facialInputFileRef = React.useRef<HTMLInputElement>(null);

  const [alertTitle, setAlertTitle] = React.useState('');
  const [openAlertDialog, setOpenAlertDialog] = React.useState(false);
  const [alertContent, setAlertContent] = React.useState('');

  const [isProcessingImage, setIsProcessingImage] = React.useState(false);
  const [startTakingDocumentImage, setStartTakingDocumentImage] = React.useState(false);
  const [isTakingBackOfDocumentImage, setIsTakingBackOfDocumentImage] = React.useState(false);
  const [isTakingFacialImage, setIsTakingFacialImage] = React.useState(false);
  const [isResultFail, setIsResultFail] = React.useState(false);

  const [documentFrontResult, setDocumentFrontResult] = React.useState<ScanDocument | undefined>(undefined);
  const [documentResult, setDocumentResult] = React.useState<ScanDocument | undefined>(undefined);
  const docCameraRef = React.useRef<HTMLVideoElement>(null);
  const facialCameraRef = React.useRef<HTMLVideoElement>(null);
  
  const [docImageData, setDocImageData] = React.useState<string | null>(null);  
  const [isDocImageCaptured, setIsDocImageCaptured] = React.useState(false);

  const [facialImageData, setFacialImageData] = React.useState<string | null>(null);
  const [isFacialImageCaptured, setIsFacialImageCaptured] = React.useState(false);

  const startDocCamera = async () => {
    try{      
      const idealSetting = getCameraDirection() === FrontCameraDirection ? FacingFrontMode : getCameraDirection() === RearCameraDirection ? FacingRearMode : FacingFrontMode;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: idealSetting },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      if(docCameraRef.current){
        docCameraRef.current.srcObject = stream;
      }
    } catch(err){
      console.error("getUserMedia not supported or denied. Falling back to file input.");
      documentInputFileRef.current?.click();
    }    
  };

  const startFacialCamera = async () => {    
    try{
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: {
          facingMode: { ideal: getCameraDirection() === FrontCameraDirection ? FacingFrontMode : getCameraDirection() === RearCameraDirection ? FacingRearMode : FacingFrontMode },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      if(facialCameraRef.current){
        facialCameraRef.current.srcObject = stream;
      }
    } catch(err){
      console.error("getUserMedia not supported or denied. Falling back to file input.");
      facialInputFileRef.current?.click();
    }    
  };

  const stopDocCamera = () => {
    const video = docCameraRef.current;

    if(video){
      const stream = video?.srcObject as MediaStream;
  
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.stop(); // Stop each track (video, audio)
        });
        video.srcObject = null; // Clear the stream from the video element
      }
    }    
  };
  
  const stopFacialCamera = () => {
    const video = facialCameraRef.current;

    if(video){
      const stream = video?.srcObject as MediaStream;
  
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.stop(); // Stop each track (video, audio)
        });
        video.srcObject = null; // Clear the stream from the video element
      }
    }    
  };

  const captureDocPhoto = () => {
    const canvas = document.createElement('canvas');
    const video = docCameraRef.current;
    if(!video) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if(ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = canvas.toDataURL('image/png');
      setDocImageData(imageData);
      setIsDocImageCaptured(true);
      stopDocCamera();
    }
  };

  const captureFacialPhoto = () => {
    const canvas = document.createElement('canvas');
    const video = facialCameraRef.current;
    if(!video) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if(ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = canvas.toDataURL('image/png');
      setFacialImageData(imageData);
      setIsFacialImageCaptured(true);
      stopFacialCamera();
    }
  };
  
  React.useEffect(() => {
    if(isTakingBackOfDocumentImage){
      startDocCamera();
    }
  },[isTakingBackOfDocumentImage]);

  React.useEffect(() => {    
    const processCapturedImage = async () => {
      setIsProcessingImage(true);
  
      try {
        if (docImageData) {
          const blob = await (await fetch(docImageData)).blob();
          const file = new File([blob], 'captured.png', { type: 'image/png' });  
          handleDocumentImage(file);         
        }
      } catch (err) {
        //handle error
        setErrorOnState();
        console.error(err);
        postErrorLog({
          confirmationNo: selectedReservation?.confirmationNo,
          errorMessage: JSON.stringify(err)
        });
      }
    };
  
    if (isDocImageCaptured && docImageData) {
      processCapturedImage();
    }
  }, [isDocImageCaptured]);

  React.useEffect(() => {
    const processCapturedImage = async () => {
      setIsProcessingImage(true);
  
      try {
        if (facialImageData) {
          const blob = await (await fetch(facialImageData)).blob();
          const file = new File([blob], 'captured.png', { type: 'image/png' });  
          handleFacialImage(file);          
        }
      } catch (err) {
        //handle error
        console.log(err);
        postErrorLog({
          confirmationNo: selectedReservation?.confirmationNo,
          errorMessage: JSON.stringify(err)
        })
      }
    };
  
    if (isFacialImageCaptured && facialImageData) {
      processCapturedImage();
    }
  }, [isFacialImageCaptured]);
  
  React.useEffect(() => {
    setStartTakingDocumentImage(false);
    setIsTakingBackOfDocumentImage(false);
    setIsTakingFacialImage(false);
    setIsProcessingImage(false);
    setIsResultFail(false);
    setDocumentFrontResult(undefined);
    setDocumentResult(undefined);
    setIsDocImageCaptured(false);
    setIsFacialImageCaptured(false);
  }, [open]);

  const setErrorOnState = () => {
    setIsProcessingImage(false);
    setStartTakingDocumentImage(false);
    setIsTakingBackOfDocumentImage(false);
    setDocumentFrontResult(undefined);
    setDocumentResult(undefined);
    setIsDocImageCaptured(false);
    setIsFacialImageCaptured(false);
    setIsResultFail(true);
  };

  const handleClose = () => {
    stopDocCamera();
    stopFacialCamera();
    onCompleted(false, null);    
  };

  const handleBack = () => {
    stopDocCamera();
    stopFacialCamera();
    if (startTakingDocumentImage) {
      setStartTakingDocumentImage(false);
      setIsTakingBackOfDocumentImage(false);
    } else if (isTakingFacialImage) {
      setIsTakingFacialImage(false);
    } else if (isResultFail) {
      setIsResultFail(false);
    } else if (isDocImageCaptured){
      setIsDocImageCaptured(false);
    } else if (isFacialImageCaptured){
      setIsFacialImageCaptured(false);
    } else {
      onCompleted(false, null);     
    }
  };

  const handleCloseAlertDialog = () => {
    setOpenAlertDialog(false);
  };

  const handleDocumentImage = async(imageFile: File) => {
    const compressedFile = await imageCompression(imageFile, imageCompressionOptions);
      if (compressedFile.size / 1024 / 1024 > 1) {
        alert(`Captured image is too large, please try again`);
        setIsProcessingImage(false);
        return;
      }

      const cameraImageBase64 = await convertFileToBase64(compressedFile);

      if (cameraImageBase64) {
        var documentResult = null;

        if (appConfig?.usingAssureId) {
          documentResult = await postDocumentForOCR(cameraImageBase64);

          documentResult.DocumentNumber = documentResult.IDNumber;
          documentResult.DocumentImage = documentResult.Image;
          documentResult.PortraitImage = documentResult.FaceImage;
          documentResult.IssueCountry = getCountryCode3ByName(documentResult.IssueCountryLong, countryList);
        } else {
          const response = await orcIdentityDocumentAPI({
            documentData: cameraImageBase64
          });

          documentResult = ConvertToScanDocument(response.result, countryList);

          if (response.resultCode !== ARIA_SuccessCode) {
            documentResult.IDType = DocumentTypeList[4].value.toUpperCase();
          }
        }

        if (documentResult != null && documentResult.IDType?.toLocaleUpperCase() != DocumentTypeList[4].value.toUpperCase()) {
          if (
            documentResult.IDType?.toLocaleUpperCase() !== DocumentTypeList[3].value.toUpperCase() &&
            !isTakingBackOfDocumentImage
          ) {            
            //check is the document a passport or ID
            setDocumentFrontResult(documentResult);
            setIsTakingBackOfDocumentImage(true);
            setStartTakingDocumentImage(true);
            setIsProcessingImage(false);
          } else if (appConfig?.reservationConfig.isFacialRecognitionRequiredForVerification) {
            //if FR is needed
            setDocumentResult(documentResult);
            setStartTakingDocumentImage(false);
            setIsProcessingImage(false);
            setIsTakingFacialImage(true);
            setIsFacialImageCaptured(false);
            startFacialCamera();
          } else {
            //Finish document scanning
            var images = [];
            if (documentResult.DocumentImage != undefined) {
              images.push(documentResult.DocumentImage);
            }

            var _document: ScanDocument = { ...documentResult, Images: images };

            if (documentFrontResult != undefined) {
              _document = combineFrontAndBackDocument(documentFrontResult, documentResult);
            }

            const user: ScanningUser = {
              document: _document,
              UserFacialImage: undefined
            };

            updateGuestToBackEndAndVerify(user);
          }
        } else {
          setErrorOnState();
        }
      }
      setIsDocImageCaptured(false);
  };

  const handleFacialImage = async(imageFile: File) => {
    const compressedFile = await imageCompression(imageFile, imageCompressionOptions);
      if (compressedFile.size / 1024 / 1024 > 1) {
        alert(`'${imageFile.name}' is too large, please pick a smaller file`);
      } else {
        const cameraImageBase64 = await convertFileToBase64(compressedFile);
        //do something with above data string

        if (cameraImageBase64 != null) {
          const livenessResult = await livenessDetection(cameraImageBase64);

          if (livenessResult.Error != null) {
            setAlertTitle('Oposss');
            setAlertContent(livenessResult.Error);
            setOpenAlertDialog(true);
          } else {
            if (livenessResult.LivenessResult != null) {
              if (documentResult != null && documentResult.PortraitImage != null) {
                const { IsMatch } = await faceMatch(cameraImageBase64, documentResult.PortraitImage);

                if (livenessResult.LivenessResult.LivenessAssessment === 'NotLive') {
                  setAlertTitle('Please Re-take a Picture');
                  setAlertContent('Selfie is not a live picture.');
                  setOpenAlertDialog(true);
                } else {
                  // setFacialResult({
                  //   isMatch: IsMatch,
                  //   matchScore: Score,
                  //   livenessResult: livenessResult.LivenessResult,
                  //   selfieImage: cameraImageBase64
                  // });

                  if (IsMatch) {
                    const faceImage: Image = {
                      Base64Image: cameraImageBase64
                    };

                    const user: ScanningUser = {
                      document: documentResult,
                      UserFacialImage: faceImage
                    };

                    onCompleted(true, user);
                  } else {
                    setAlertTitle('Please Re-take a Picture');
                    setAlertContent('Selfie is not a live picture.');
                    setOpenAlertDialog(true);
                  }
                }
              }
            }
          }

          setIsTakingFacialImage(false);
          setIsProcessingImage(false);
        }
      }
  };

  const openPrivacyPolicyPopUp = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAlertTitle(getVerbiage(verbiages, 'weblbl031'));
    setAlertContent(getVerbiage(verbiages, 'weblbl032'));
    setOpenAlertDialog(true);
  };

  const selectTakeDocumentAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    setStartTakingDocumentImage(true);
    // documentInputFileRef.current?.click();
    if(getApplicationMode()===KioskApplicationMode){
      setIsDocImageCaptured(false);
      startDocCamera();
    }
  };

  const selectTakeDocument = (event: React.MouseEvent<HTMLButtonElement>) => {    
    if(getApplicationMode()===KioskApplicationMode){
      captureDocPhoto();
    }
    else{
      documentInputFileRef.current?.click();
    }
  };

  const selectTakeSelfie = (event: React.MouseEvent<HTMLButtonElement>) => {
    if(getApplicationMode()===KioskApplicationMode){
      captureFacialPhoto();
    }
    else{
      facialInputFileRef.current?.click();
    }    
  };

  const onInputChangeForDocument = async (event: React.FormEvent<HTMLInputElement>) => {
    setIsProcessingImage(true);
    const files: Array<File> = Array.prototype.slice.call(event.currentTarget.files);

    if (files != null) {
      const errs = checkSupportedUploadImage(files);

      if (errs.length) {
        setIsProcessingImage(false);
        const err = errs.pop();
        if (err != undefined) {
          setAlertTitle(err?.name);
          setAlertContent(err?.message);
          setOpenAlertDialog(true);
        }
      } else {
        if (files && files.length) {
          try {
            const imageFile = files[0];
            //File compression
            handleDocumentImage(imageFile);
          } catch (err) {
            //handle error
            setErrorOnState();
            console.log(err);
            postErrorLog({
              confirmationNo: selectedReservation?.confirmationNo,
              errorMessage: JSON.stringify(err)
            })
          }
        }
      }
    }
  };

  const updateGuestToBackEndAndVerify = async (guest: ScanningUser) => {
    if (guest && guest.document && selectedHotel) {
      //updating user
      if (updateUser != undefined) {
        const guestInfo = convertToUpdateGuestRequestModel(
          guest.document,
          selectedHotel,
          updateUser.profileId,
          countryList
        );
        try {
          const response: UpdateGuestResponseModel = await updateGuestAPI(guestInfo);
          if (response.resultCode == ARIA_SuccessCode) {
            if (selectedHotel != undefined && selectedReservation != undefined) {
              const _user = response.result;

              var _updatingReservation = selectedReservation.sharedReservations?.find(
                (res) => res.guest.profileId === _user.profileId
              );
              if (_updatingReservation == undefined && selectedReservation.guest.profileId === _user.profileId) {
                _updatingReservation = selectedReservation;
              }

              dispatch(updateReservationGuest(_user));

              if (_updatingReservation != undefined) {
                verifyReservationAPI({
                  confirmationNo: _updatingReservation.confirmationNo,
                  guestId: _user.id,
                  primaryConfirmationNo: selectedReservation.confirmationNo,
                  verified: true
                })
                  .then(() => {
                    dispatch(reservationVerified(_user));
                    onCompleted(true, null);
                  })
                  .catch((err) => {
                    console.log(err);
                    setErrorOnState();
                    postErrorLog({
                      confirmationNo: selectedReservation?.confirmationNo,
                      errorMessage: JSON.stringify(err)
                    })
                  });
              } else {
                setErrorOnState();
              }
            }
          } else {
            setErrorOnState();
          }
        } catch (err) {
          setErrorOnState();
          postErrorLog({
            confirmationNo: selectedReservation?.confirmationNo,
            errorMessage: JSON.stringify(err)
          })
        }
      } else {
        //Create User
        const guestInfo = convertToCreateGuestRequestModel(guest.document, selectedHotel, countryList);
        await dispatch(addNewGuestToReservation({ guestInfo: guestInfo }));
      }
    }
  };

  const onInputChangeForSelfie = async (event: React.FormEvent<HTMLInputElement>) => {
    setIsProcessingImage(true);
    const files: Array<File> = Array.prototype.slice.call(event.currentTarget.files);
    if (files != null) {
      const errs = checkSupportedUploadImage(files);

      if (errs.length) {
        setIsProcessingImage(false);
        const err = errs.pop();
        if (err != undefined) {
          setAlertTitle(err?.name);
          setAlertContent(err?.message);
          setOpenAlertDialog(true);
        }
      } else {
        if (event.currentTarget.files && event.currentTarget.files.length) {
          try {
            const imageFile = event.currentTarget.files[0];
            handleFacialImage(imageFile);            
          } catch (err) {
            //handle error
            console.log(err);
            postErrorLog({
              confirmationNo: selectedReservation?.confirmationNo,
              errorMessage: JSON.stringify(err)
            })
          }
        }
      }
    }
  };

  return (
    <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition} {...rest}>
      <AppBar className={classes.appBar}>
        <Toolbar>
          {!isProcessingImage ? (
            <IconButton hidden={isProcessingImage} edge="start" color="inherit" onClick={handleBack} aria-label="close">
              <ArrowBackIosIcon />
            </IconButton>
          ) : null}
          <Typography variant="h6" className={classes.title}>
            {/* processing document / update guest data */}
            {isProcessingImage ? getVerbiage(verbiages, 'weblbl027') : getVerbiage(verbiages, 'weblbl026')}
          </Typography>
          {!isProcessingImage ? (
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          ) : null}
        </Toolbar>
      </AppBar>

      <Toolbar />

      <GridContainer>
        <GridItem xs={12}>
          <PopUp
            title={alertTitle}
            content={alertContent}
            open={openAlertDialog}
            onClose={handleCloseAlertDialog}
            buttonStyle="alert"
            onPositiveButtonClick={handleCloseAlertDialog}
          />
        </GridItem>
      </GridContainer>

      {isProcessingImage ? (
        <GridContainer
          spacing={2}
          direction="row"
          justifyContent="center"
          alignContent="center"
          style={{ minHeight: '80vh' }}
        >
          <GridItem xs={12}>
            <Typography align="center" variant="body1">
              <b>
                {/* Please do not exit the application while we are processing your document. */}
                {getVerbiage(verbiages, 'weblbl033')}
              </b>
            </Typography>
          </GridItem>
          <GridItem xs={12} style={{ textAlign: 'center' }}>
            <img src={imageProcessIcon} alt={imageProcessIcon} style={{ maxWidth: '75%' }} />
          </GridItem>
          <GridItem xs={12}>
            <Typography align="center" variant="h6">
              {/* PROCESSING... */}
              <b>{getVerbiage(verbiages, 'weblbl034')}</b>
            </Typography>
          </GridItem>
        </GridContainer>
      ) : isResultFail ? (
        <GridContainer direction="column" justifyContent="center" alignItems="center" style={{ minHeight: '80vh' }}>
          <GridItem>
            <CancelIcon color="error" />
          </GridItem>
          <GridItem>
            <Typography align="center" variant="subtitle1">
              {/* Invalid document */}
              <b>{getVerbiage(verbiages, 'weblbl035')}</b>
            </Typography>
          </GridItem>
          <GridItem>
            <Typography align="center" variant="body1">
              {/* Your document is not a valid document please scan your document again. */}
              <b>{getVerbiage(verbiages, 'weblbl036')}</b>
            </Typography>
          </GridItem>
          <GridItem style={{ marginTop: '48px' }}>
            {/* Take A Picture */}
            <CustomButton onClick={() => setIsResultFail(false)}>{getVerbiage(verbiages, 'webbtn010')}</CustomButton>
          </GridItem>
        </GridContainer>
      ) : startTakingDocumentImage ? (
        <GridContainer
          spacing={2}
          justifyContent="center"
          alignContent="center"
          direction="row"
          style={{ minHeight: '80vh' }}
        >
          <GridItem xs={12}>
            <Typography align="center" variant="body1">
              {isTakingBackOfDocumentImage ? (
                <b>{getVerbiage(verbiages, 'Please take a picture of your back document')}</b>
              ) : (
                <b>{getVerbiage(verbiages, 'weblbl037')}</b>
              )}
            </Typography>
          </GridItem>
          {getApplicationMode() === KioskApplicationMode ? 
            (
              <GridItem xs={12} style={{ textAlign: 'center' }}>
                <video ref={docCameraRef} autoPlay playsInline muted style={{ width: 'auto', height: '75vh' }} />
                <div
                  style={{
                    position: "absolute",
                    top: "55%",
                    left: "50%",
                    width: "25%",
                    height: "33%",
                    border: "3px solid red",
                    borderRadius: 10,
                    boxSizing: "border-box",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              </GridItem>
            )
             :            
            (
              <>
                <input
                  hidden
                  type="file"
                  ref={documentInputFileRef}
                  onChange={onInputChangeForDocument}
                  accept="image/*"
                  capture="environment"
                ></input>
                <GridItem xs={12} style={{ textAlign: 'center' }}>
                  <img src={documentIcon} alt={documentIcon} style={{ maxWidth: '80%' }} />
                </GridItem>
              </>
            )
          }
          <GridItem xs={12} style={{ textAlign: 'center' }}>
            <CustomButton variant="contained" color="primary" onClick={selectTakeDocument}>
              {getVerbiage(verbiages, 'webbtn010')} {/*Take a Picture*/ }
            </CustomButton>
          </GridItem>
        </GridContainer>
      ) : isTakingFacialImage ? (
        <GridContainer
          spacing={2}
          justifyContent="center"
          alignContent="center"
          direction="row"
          style={{ minHeight: '80vh' }}
        >
          <GridItem xs={12} style={{ maxWidth: '80%' }}>
            <Typography align="center" variant="body1">
              {/* Please take a picture of yourself to verify document. */}
              <b>{getVerbiage(verbiages, 'weblbl038')} </b>
            </Typography>
          </GridItem>
          {getApplicationMode() === KioskApplicationMode ? 
            (
              <GridItem xs={12} style={{ textAlign: 'center' }}>
                <video ref={facialCameraRef} autoPlay playsInline style={{ width: 'auto', height: '75vh' }} />
              </GridItem>
            )
             :            
            (
              <>
               <input
                hidden
                type="file"
                ref={facialInputFileRef}
                onChange={onInputChangeForSelfie}
                accept="image/*"
                capture="user"
              ></input>
              <GridItem xs={12} style={{ textAlign: 'center' }}>
                <img src={facialIcon} alt={facialIcon} style={{ maxWidth: '80%' }} />
              </GridItem>
              </>
            )
          }

          <GridItem xs={12} style={{ textAlign: 'center' }}>
            <CustomButton variant="contained" color="primary" onClick={selectTakeSelfie}>
              {getVerbiage(verbiages, 'webbtn010')}
            </CustomButton>
          </GridItem>
        </GridContainer>
      ) : (
        //Initail View
        <GridContainer
          direction="column"
          spacing={3}
          alignContent="center"
          alignItems="flex-start"
          style={{ padding: '18px' }}
        >
          <GridItem>
            <Card elevation={3}>
              <CardActionArea onClick={selectTakeDocumentAction}>
                <CardContent>
                  <GridContainer spacing={1} alignItems="center" justifyContent="space-between">
                    <GridItem xs={9}>
                      <Typography variant="h6">{getVerbiage(verbiages, 'weblbl028')}</Typography> {/*Camera*/ }
                      <Typography variant="body2" color="textSecondary">
                        {getVerbiage(verbiages, 'weblbl029')}{/*Use camera to obtain document data from your passport/NRIC/FIN*/ }
                      </Typography>
                    </GridItem>
                    <GridItem style={{ justifyContent: 'center', alignItems: 'center' }}>
                      <CameraAltIcon fontSize="inherit" style={{ fontSize: '3.5rem' }} />
                    </GridItem>
                  </GridContainer>
                </CardContent>
              </CardActionArea>
            </Card>
          </GridItem>
          <GridItem>
            {/* Button for Privacy Policy */}
            <Typography variant="body2">
              {getVerbiage(verbiages, 'weblbl030')}
              <Button onClick={openPrivacyPolicyPopUp} size="small" variant="text" style={{ textTransform: 'none' }}>
                <u>{getVerbiage(verbiages, 'webbtn009')}</u>
              </Button>
            </Typography>
          </GridItem>
        </GridContainer>
      )}
    </Dialog>
  );
};
